import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { motion, useTransform, type MotionValue } from "motion/react";
import escape from "lodash/escape";
import { useWindowSize, WindowSize } from "@react-hookz/web";
import { ZERO_WIDTH_SPACE } from "@utils/ZERO_WIDTH_SPACE";
import { RevealAnimationVariants } from "@utils/RevealAnimationVariants";

type AnimationType = keyof typeof RevealAnimationVariants;

export const AnimatedSplitLines = ({
  children,
  center = false,
  value,
  id,
  animationType = "clipPath",
  underlyingTextClassNames = "opacity-0",
  stagger = animationType === "clipPathReveal",
}: {
  children: ReactNode;
  center?: boolean;
  value: MotionValue<number>; // MUST BE FROM -1 to 1
  id?: string;
  animationType?: AnimationType;
  underlyingTextClassNames?: string;
  stagger?: boolean;
}) => {
  const [splitChildren, setSplitChildren] = useState<Array<string>>([]);
  const referenceElementRef = useRef<HTMLSpanElement>(null);
  const windowSize = useWindowSize();

  if (typeof children !== "string" && animationType !== "clipPath") {
    throw new Error(
      `AnimatedSplitLines: Using animationType="${animationType}" with children of type JSX.Element will not work on all screen sizes.`
    );
  }

  useEffect(() => {
    const referenceElement = referenceElementRef.current;
    if (!referenceElement) return;

    const buildSplitChildren = (
      element: HTMLElement,
      splitChildren: Array<string> = [],
      context = (text: string) => text
    ) => {
      [...element.childNodes].map((node) => {
        if (isTextNode(node)) {
          splitChildren.push(
            ...extractLinesFromTextNode(node)
              .map((text) => {
                // some weird stuff can happen with the clip animation if
                // a line starts with a space... and that will technically
                // still happen here, except we don't care if the animation
                // doesn't properly work for an individual space
                return [
                  ...(text.startsWith(" ") ? ["<span> </span>"] : []),
                  context(escape(text.trim())),
                  ...(text.endsWith(" ") ? ["<span> </span>"] : []),
                ];
              })
              .flat()
          );
        } else if (node instanceof HTMLElement) {
          if (node.tagName === "BR") {
            splitChildren.push("<br/>");
          } else {
            buildSplitChildren(node, splitChildren, (text: string) => {
              const [open, close] = (
                node.cloneNode() as typeof node
              ).outerHTML.split("><");
              return context(`${open}>${text}<${close}`);
            });
          }
        }
      });

      return splitChildren;
    };

    setSplitChildren(buildSplitChildren(referenceElement));

    const resizeObserver = new ResizeObserver(() => {
      setSplitChildren(buildSplitChildren(referenceElement));
    });

    resizeObserver.observe(referenceElement.parentElement!);

    return () => resizeObserver.disconnect();
  }, [windowSize]);

  const splitChildrenMinusSpacers = useMemo(
    () => splitChildren.filter((child) => child !== "<span> </span>"),
    [splitChildren]
  );

  return (
    <span className={cx("relative block", center && "text-center")}>
      <span
        className={cx(
          underlyingTextClassNames,
          animationType !== "clipPathReveal" && "inline-block"
        )}
        id={id}
        ref={referenceElementRef}
      >
        {children}
      </span>
      <span
        aria-hidden
        aria-label=""
        className="absolute top-0 left-0 size-full highlight select-none pointer-events-none"
      >
        {splitChildren.map((child, index) => (
          <SplitChild
            key={index}
            child={child}
            index={splitChildrenMinusSpacers.indexOf(child)}
            total={splitChildrenMinusSpacers.length}
            windowSize={windowSize}
            value={value}
            animationType={animationType}
            stagger={stagger}
          />
        ))}
      </span>
    </span>
  );
};

const SplitChild = ({
  child,
  index,
  total,
  value,
  windowSize,
  animationType,
  stagger,
}: {
  child: string;
  index: number;
  total: number;
  value: MotionValue;
  windowSize: WindowSize;
  animationType: AnimationType;
  stagger: boolean;
}) => {
  const { variant, property, cssProperty } =
    RevealAnimationVariants[animationType];
  const animationValue = useTransform(
    value,
    stagger
      ? [-1, (-total + index) / total, (-total + index + 1) / total, 1]
      : [-1, -0.5, 0, 0.5, 1],
    stagger
      ? [variant.start, variant.start, variant.visible, variant.visible]
      : [
          variant.start,
          variant.start,
          variant.visible,
          variant.hidden,
          variant.hidden,
        ]
  );

  return (
    <span
      // sometimes clipPath janks on on window-resize, so we have to force
      // a rerender for these elements per windowSize
      key={`${index}-wh-${windowSize.height}-ww-${windowSize.width}`}
      className={cx(
        animationType === "translateY" && "overflow-y-hidden block"
      )}
    >
      <motion.span
        style={{ [property]: animationValue, willChange: cssProperty }}
        className={cx(animationType === "translateY" && "block")}
      >
        <span dangerouslySetInnerHTML={{ __html: child }}></span>
      </motion.span>
    </span>
  );
};

const isTextNode = (node: Node | null): node is Text => {
  return node?.nodeType === Node.TEXT_NODE;
};

// concepts here utilized from
// https://www.bennadel.com/blog/4310-detecting-rendered-line-breaks-in-a-text-node-in-javascript.htm
const collapseWhiteSpace = (value: string) =>
  value
    .replace(/(\s)+/g, " ")
    .replace(new RegExp(`(${ZERO_WIDTH_SPACE})+`, "g"), ZERO_WIDTH_SPACE);

const extractLinesFromTextNode = (textNode: Text) => {
  textNode.textContent = collapseWhiteSpace(textNode.textContent ?? "");
  const textContent = textNode.textContent ?? "";
  const range = document.createRange();
  const lines: Array<Array<string>> = [[]];

  for (let index = 0; index < textContent.length; index++) {
    range.setStart(textNode, 0);
    range.setEnd(textNode, index + 1);

    const lineIndex = range.getClientRects().length - 1;

    if (!lines[lineIndex]) {
      lines.push([]);
    }

    lines[lines.length - 1].push(textContent.charAt(index));
  }

  return lines.map((characters) => collapseWhiteSpace(characters.join("")));
};
